import { t } from 'i18next';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useRecoilState, useResetRecoilState } from 'recoil';
import ErrorMessage from 'src/app/components/ErrorMessage/ErrorMessage';
import Footer from 'src/app/components/Footer/Footer';
import { LanguageSelectorModal } from 'src/app/components/LanguageSelectorModal/LanguageSelectorModal';
import { LoadingOverlay } from 'src/app/components/LoadingOverlay/LoadingOverlay';
import PinList from 'src/app/components/PinList/PinList';
import { ResumeConversationModal } from 'src/app/components/ResumeConversationModal/ResumeConversationModal';
import useChatAction from 'src/app/hooks/network/useChatAction';
import useCheckExercise from 'src/app/hooks/network/useCheckExercise';
import useSendConversation from 'src/app/hooks/network/useSendConversation';
import useSMEConversation from 'src/app/hooks/network/useSMEConversation';
import useStartConversation from 'src/app/hooks/network/useStartConversation';
import {
  conversationsAtom,
  pinMinimizeAtom,
  pinWindowAtom,
  promptAtom,
  selectedLangAtom,
  tutorStateAtom,
  userContentAtom,
} from 'src/app/state/atoms';
import { testIds } from 'src/app/constants';
import { getRuntimeEnvironment } from 'src/app/utils/getRuntimeEnvironment';
import { ReactComponent as PinFillSVG } from 'src/assets/images/pin-fill.svg';
import styles from './ChatScreen.module.scss';
import Conversations from './Conversations/Conversations';

function ChatScreen() {
  const [prompt, setPrompt] = useRecoilState(promptAtom);
  const [conversations] = useRecoilState(conversationsAtom);
  const [, setUserContent] = useRecoilState(userContentAtom);
  const [tutorState] = useRecoilState(tutorStateAtom);
  const resetPrompt = useResetRecoilState(promptAtom);
  const [showPinWindow] = useRecoilState(pinWindowAtom);
  const [isMinimized, setIsMinimized] = useRecoilState(pinMinimizeAtom);
  const [selectedLang, setSelectedLang] = useRecoilState(selectedLangAtom);
  const [showResumeModal, setShowResumeModal] = React.useState(false);
  const [showLanguageModal, setShowLanguageModal] = React.useState(false);

  const handleSendMessage = useSendConversation();
  const fetchConversation = useStartConversation();
  const fetchSMEConversation = useSMEConversation();
  const fetchConversationHistory = useChatAction();
  const { checkExerciseRequest, checkExercise } = useCheckExercise();

  // Object destructuring ...
  const { loading, data: exerciseData } = checkExercise;
  const { isHistoryMode, isSMEMode } = tutorState;

  const handleStaleCallback = React.useCallback((callback: () => void) => {
    callback();
  }, []);

  // Fetch: exercise response
  useEffect(() => {
    handleStaleCallback(checkExerciseRequest);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render: conversation history block
  useEffect(() => {
    // If isHistoryMode -> Fetch conversation history
    if (isHistoryMode) {
      handleStaleCallback(fetchConversationHistory);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHistoryMode]);

  // Render: SME block
  useEffect(() => {
    if (!loading && !isHistoryMode && isSMEMode) {
      handleStaleCallback(fetchSMEConversation);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isHistoryMode, isSMEMode]);

  // Render: conversation block
  useEffect(() => {
    if (!loading && !isHistoryMode && !isSMEMode) {
      const conversationIds = exerciseData?.conversation_ids ?? [];
      if (conversationIds?.length) {
        setShowResumeModal(true);
      } else {
        handleStaleCallback(fetchConversation);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isHistoryMode, isSMEMode, exerciseData?.conversation_ids]);

  // Render: start conversation block
  useEffect(() => {
    if (!loading && !isHistoryMode) {
      if (selectedLang) {
        if (isSMEMode) {
          handleStaleCallback(fetchSMEConversation);
        } else {
          handleStaleCallback(fetchConversation);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSMEMode, selectedLang, isHistoryMode]);

  const handleConversationSelect = (conversationId: string | null) => {
    setShowResumeModal(false);
    if (conversationId) {
      fetchConversation(conversationId);
    } else {
      // Start Over - no conversation ID
      fetchConversation();
    }
  };

  const handleStartOver = () => {
    setShowResumeModal(false);
    fetchConversation();
  };

  const handleOnCancel = () => {
    setShowLanguageModal(false);
    if (isSMEMode) {
      handleStaleCallback(fetchSMEConversation);
    } else {
      handleStaleCallback(fetchConversation);
    }
  };

  const handleLangSelection = (selectedLangCode: string) => {
    setSelectedLang(() => selectedLangCode);
    setShowLanguageModal(false);
  };

  const windowWidth = useMemo(() => window.innerWidth, []);
  const chatWindowWidth = showPinWindow ? windowWidth : 'auto';
  const bgColor = 'white';
  const screenRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerDocked, setFooterDocked] = useState(true);

  // Compare the footer to this pane, not the browser window. The view is iframed.
  // At or under half the pane, the footer sticks to the bottom. Above that it
  // stays in flow after the messages. The message list itself does not scroll.
  useLayoutEffect(() => {
    const screen = screenRef.current;
    if (!screen) return;

    const syncFooterPlacement = () => {
      const footer = footerRef.current;
      const available = screen.clientHeight;
      const footerHeight = footer?.offsetHeight ?? 0;
      const dock = !footer || available <= 0 || footerHeight <= available * 0.7;
      const offset = dock ? `${footerHeight}px` : '0px';

      setFooterDocked(dock);
      if (screen.style.getPropertyValue('--chat-footer-offset') !== offset) {
        screen.style.setProperty('--chat-footer-offset', offset);
      }
    };

    syncFooterPlacement();
    const observer = new ResizeObserver(syncFooterPlacement);
    observer.observe(screen);
    if (footerRef.current) observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, [isHistoryMode]);

  return (
    <ErrorBoundary fallback={<ErrorMessage errorMessage={t('APP_CRASHED')} />}>
      <div ref={screenRef} id={testIds.chatScreen} className={styles.chatScreen}>
        <div className={`${styles.chatBody} ${showPinWindow ? styles.withPins : ''}`}>
          {showPinWindow && (
            <div className={`${styles.pinWindow}`}>
              <PinList />
            </div>
          )}
          {isMinimized && (
            <div className={`${styles.pinMinimized} group`}>
              <>
                <PinFillSVG
                  className='inline-block mr-space-2'
                  fill={bgColor}
                  onClick={() => setIsMinimized(false)}
                />
                <span className='absolute top-10 left-5 scale-0 rounded bg-gray-800 p-space-2 text-xs text-white group-hover:scale-100'>
                  {t('MAXIMIZE')}
                </span>
              </>
            </div>
          )}

          {/* enable loading state for exercise data */}
          {loading && <LoadingOverlay />}

          {/* enable conversation selection modal */}
          {showResumeModal && (
            <ResumeConversationModal
              onSelectConversation={handleConversationSelect}
              onStartOver={handleStartOver}
            />
          )}

          {/* enable language selector modal */}
          {showLanguageModal && (
            <LanguageSelectorModal onSelect={handleLangSelection} onCancel={handleOnCancel} />
          )}

          <div className={styles.conversationColumn} style={{ width: chatWindowWidth }}>
            <Conversations />
          </div>
        </div>

        {!isHistoryMode && (
          <div ref={footerRef} className={footerDocked ? styles.footerDock : styles.footerAttached}>
            <Footer
              prompt={prompt}
              disablePrompt={isSMEMode}
              conversationId={
                getRuntimeEnvironment() === 'prod' ? undefined : conversations.data.id
              }
              onPromptInputChange={(event) => setPrompt(event.target.value)}
              onPromptSend={() => {
                setUserContent(prompt);
                handleSendMessage(prompt);
                resetPrompt();
              }}
              hiddenWithDisabledFocus={conversations.loading}
            />
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default ChatScreen;
